import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { useAsyncFn } from 'react-use';
import { bindActionCreators } from 'redux';

import { ibeStylistPermissionsUpdate } from 'actions/user/beStylistPermissions.action';
import { determineChoiceBasedCheckboxValue, isAllStaffNoAccessCheckboxChoices, isChoiceBasedCheckboxChecked } from 'bl/access/determineChoiceBasedCheckboxValue.bl';
import TypedOption from 'components/form/TypedOption.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Icon from 'components/generic/Icon.component';
import PERM_FORMAT from 'constants/form/permFormat.const';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useCurrentSalonId } from 'providers/SalonBasicInfo.provider';
import noNilArray from 'utils/arrays/noNilArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import refreshLockToken from 'utils/core/refreshLockToken.util';
import logger from 'utils/logger.util';

import CN from './PermissionField.module.scss';

const L = logger('PermissionField');

const PermissionField = ({ stylistId, value: valueFromProps, definition, tooltip, stylistHasColumn = true, ibeStylistPermissionsUpdate }) => {
  const { t } = useTranslation();
  const salonId = useCurrentSalonId();

  const label = fromAnyToTrimmed(definition?.label);

  const enabled = definition?.enabled;
  const disabled = !definition?.enabled;

  const permFormat = definition?.format;
  const permKey = definition?.key;
  const choiceBasedCheckboxChoices = useMemo(() => {
    const choices = noNilArray(definition?.choices);
    return isAllStaffNoAccessCheckboxChoices(choices) ? choices : null;
  }, [definition?.choices]);
  const isChoiceBasedCheckbox = permFormat === PERM_FORMAT.checkbox && choiceBasedCheckboxChoices != null;

  // the local value from updating should be the last state from BE, so it takes precedence to the one from props
  const [localValue, setLocalValue] = useStateWhileMounted();
  const value = localValue ?? valueFromProps;

  const isCheckboxChecked = isChoiceBasedCheckbox ? isChoiceBasedCheckboxChecked(value?.[permKey]) : fromAnyToBool(value?.[permKey]);

  const { val: renderAlert, yay: showAlert, nay: hideAlert } = useTip(false);

  const [{ value: valueFromResult, loading: isLoading, error }, handleCheck] = useAsyncFn(
    async (ev) => {
      // checkoxes are simple, but with the radios, we give the key of the choice as the value of the permission
      const newValue = isChoiceBasedCheckbox ? determineChoiceBasedCheckboxValue(isCheckboxChecked) : permFormat === PERM_FORMAT.checkbox ? !isCheckboxChecked : fromAnyToTrimmed(ev?.name);
      const data = { [permKey]: newValue };

      const result = await ibeStylistPermissionsUpdate({ stylistId, data });

      L.debug('handleCheck()', result, '<-', { stylistId, data, permKey, permFormat, ev, ibeStylistPermissionsUpdate });

      if (!result.is.ok) {
        // TODO: handle errors, display to user or at the very least to the browser console
        return null;
      }

      setLocalValue(result.json);
      showAlert();
      refreshLockToken({ salonId });
      return result.json;
    },
    [ibeStylistPermissionsUpdate, isCheckboxChecked, isChoiceBasedCheckbox, permFormat, permKey, salonId, setLocalValue, showAlert, stylistId],
  );

  useEffect(() => {
    if (renderAlert) {
      const timer = setTimeout(() => {
        hideAlert();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [hideAlert, renderAlert]);

  if (error) L.warn('()', isLoading, error, value, { localValue, valueFromProps, valueFromResult });

  return (
    <React.Fragment>
      {renderAlert && (
        <AlertPanel tag="PermissionField__AlertPanel" variant="success" position="centerTopFixed">
          <Icon variant="confirm" width="lg" />
          {t('staffAndBusiness.stylistPermission.alertMessage')}
        </AlertPanel>
      )}
      {permFormat === PERM_FORMAT.radio ? (
        noNilArray(definition?.choices)
          .filter(({ key: k }) => !(permKey === 'can_view_calendar' && k === 'no_access'))
          .map(({ key: choiceKey, label: choiceLabel }) => {
            // unlike the checkbox, the radios are all different choices under a same permission key
            const isChoiceChecked = choiceKey === value?.[permKey];
            const tag = `${permKey}:${choiceKey}`;
            const isNoColumnPersonalBlock = permKey === 'can_view_calendar' && choiceKey === 'personal' && !stylistHasColumn;
            const choiceDisabled = disabled || isNoColumnPersonalBlock;
            const choiceTooltip = isNoColumnPersonalBlock && !disabled ? t('staffAndBusiness.stylistPermission.noColumnPersonalCalendarTooltip') : tooltip;

            return (
              <React.Fragment key={choiceKey}>
                <TypedOption key={choiceKey} type="tooltip-radio" name={choiceKey} tag={tag} checked={isChoiceChecked} onCheck={handleCheck} disabled={choiceDisabled} tooltip={choiceTooltip}>
                  <div data-bem="PermissionField__label--tooltip-radio" className={CN.name}>
                    {choiceLabel}
                  </div>
                </TypedOption>
              </React.Fragment>
            );
          })
      ) : enabled ? (
        <TypedOption type="simple-checkbox" name={permKey} checked={isCheckboxChecked} onCheck={handleCheck} disabled={disabled}>
          <div data-bem="PermissionField__label--simple-checkbox" className={CN.name}>
            {label}
          </div>
        </TypedOption>
      ) : (
        <TypedOption type="tooltip-checkbox" name={permKey} checked={isCheckboxChecked} onCheck={handleCheck} disabled={disabled} tooltip={tooltip}>
          <div data-bem="PermissionField__label--tooltip-checkbox" className={CN.name}>
            {label}
          </div>
        </TypedOption>
      )}
    </React.Fragment>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ibeStylistPermissionsUpdate,
    },
    dispatch,
  );

const PermissionFieldHoc = connect(null, mapDispatchToProps)(PermissionField);

export default PermissionFieldHoc;
